// Import from libraries
import { useState, useEffect, useMemo } from 'react'
import { useParams } from '@tanstack/react-router'
import {
    type GridDataStateChangeEvent,
    type GridColumnProps 
} from '@progress/kendo-react-grid';
import { type State } from '@progress/kendo-data-query';

// Import from scripts
import { useExtractsQuery } from '@/queries.ts'
import { type ExtractRequest } from '@/models/extract'
import { extractRecordTypes } from '@/models'

// Import components
import Card from '@/components/shared/Card'
import KendoTableServerState from '@/components/shared/tables/KendoTableServerState'
import ExtractStatusCell from './ExtractStatusCell'
import TableDateCell from '@/components/shared/tables/TableDateCell'
import CategoryFilterCell from '@/components/shared/tables/CategoryFilterCell'
import { DateFilterCell } from '@/components/shared/tables/GridFilterCells'

const REFETCH_INTERVAL = 10000; // 10 seconds

export default function ExtractsCard() {
    const params = useParams({ from: '/$leaid' })

    const [dataState, setDataState] = useState<State>({ 
        take: 10, 
        skip: 0, 
        sort: [{ field: 'RequestDate', dir: 'desc' }] 
    });
    const [isInitialLoadDone, setIsInitialLoadDone] = useState<boolean>(false);

    const {
        data: tableData,
        error,
        isLoading
    } = useExtractsQuery(params.leaid, dataState, REFETCH_INTERVAL);

    // const tableData = data?.data ?? [];

    // Note: using custom variable to track one-time initial load done, because none of tanstack 
    // query's status return values (isPending, isFetching, isFetched) seemed to do this??
    // Fixes error of table rendering before data is fully fetched.
    useEffect(() => {
        if (!isInitialLoadDone && !isLoading)
            setIsInitialLoadDone(true);
    }, [isLoading, isInitialLoadDone]);

    const dataStateChange = (e: GridDataStateChangeEvent) => {
        setDataState(e.dataState);
    };

    const statusOptions = ['Complete', 'Request Raised'];


    const columns: GridColumnProps[] = useMemo(() => [
        {
            id: 'FileName',
            field: 'FileName',
            title: "Filename",
            width: 250
        },
        // {
        //     id: 'ExtractRequestId',
        //     field: 'ExtractRequestId',
        //     title: "Id",
        //     filter: "numeric",
        // },
        {
            id: 'ExtractType',
            field: 'ExtractType',
            title: "Record Type",
            cells: {
                filterCell: (props) => CategoryFilterCell(props, [...extractRecordTypes])
            },
        },
        {
            id: 'RequestDate',
            field: 'RequestDate',
            title: "Date Requested",
            filter: "date",
            cells: {
                data: TableDateCell,
                filterCell: (props) => DateFilterCell(props, "Date Requested")
            },
        },
        {
            id: 'RequestorUsername',
            field: 'RequestorUsername',
            title: "Requested By",
            width: 250
        },
        {
            id: 'FileSizeBytes',
            field: 'FileSizeBytes',
            title: "File Size (bytes)",
            filter: "numeric",
        },
        {
            id: 'ExtractStatus',
            field: 'ExtractStatus',
            title: "Status",
            cells: {
                data: ({ dataItem, tdProps }) => ExtractStatusCell(dataItem as ExtractRequest, tdProps),
                filterCell: (props) => CategoryFilterCell(props, [...statusOptions])
            },
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [])

    return (
        <Card className="mt-4 mb-3 no-padding card-border">
            <div
                className="text-white p-3 d-flex align-items-center gap-2"
                style={{
                    background: "var(--color-p3)",
                    fontWeight: 500,
                    borderTopLeftRadius: "inherit",
                    borderTopRightRadius: "inherit",
                }}
            >
                ODS Exports
            </div>
            {isInitialLoadDone &&
                <KendoTableServerState
                    data={tableData}
                    columns={columns}
                    autoProcessData={false}
                    {...dataState}
                    error={error}
                    onDataStateChange={dataStateChange}
                />
            }
        </Card>
    )
}